import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { errText } from "@/lib/aadhaar";

// Public page opened from the QR code on a Class Star certificate.
export default function VerifyStar() {
  const { L } = useLang();
  const { code } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/class-star/verify/${encodeURIComponent(code)}`).then((x) => setR(x.data))
      .catch((e) => setErr(errText(e.response?.data?.detail, L("No Class Star badge with this number", "इस नंबर का कोई क्लास स्टार बैज नहीं"))));
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-[75vh] rs-radial grid place-items-center px-5 py-14 text-[#0A192F]" data-testid="verify-star-page">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white/90 backdrop-blur-xl p-8">
        {!r && !err && <Loader2 className="w-8 h-8 animate-spin text-[#007A8A] mx-auto" data-testid="verify-star-loading" />}
        {err && (
          <div className="text-center" data-testid="verify-star-invalid">
            <ShieldX className="w-14 h-14 text-[#FF3B3B] mx-auto" />
            <h1 className="mt-3 text-2xl font-extrabold">{L("Not verified", "सत्यापित नहीं")}</h1>
            <p className="text-slate-500 text-sm mt-1">{err}</p>
          </div>
        )}
        {r && (
          <div data-testid="verify-star-result">
            <div className="text-center">
              <ShieldCheck className="w-14 h-14 text-emerald-600 mx-auto" />
              <h1 className="mt-3 text-2xl font-extrabold" data-testid="verify-star-status">{L("Verified Class Star", "सत्यापित क्लास स्टार")}</h1>
              <div className="mt-2 flex justify-center gap-0.5" aria-label="5/5">{[0, 1, 2, 3, 4].map((k) => <Star key={k} className="w-6 h-6 fill-[#FFC94D] text-[#B45309]" />)}</div>
              <p className="mt-2 text-sm text-slate-500">{L("This student really earned all 5 stars in a RoboSpark virtual class: present, 3/3 quiz checked by the server and 90%+ of the lesson watched.", "इस छात्र ने RoboSpark वर्चुअल क्लास में सच में पूरे 5 स्टार कमाए: उपस्थित, सर्वर से जाँचा 3/3 क्विज़ और पाठ का 90%+ देखा।")}</p>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {[[L("Name", "नाम"), r.student_name, "name"], [L("School", "स्कूल"), r.school_name || "—", "school"], [L("Class", "कक्षा"), r.class_grade || "—", "class"],
                [L("Badge no.", "बैज नं."), r.badge_no, "badge"], [L("Lesson", "पाठ"), L(`${r.lesson.n} · ${r.lesson.title}`, `${r.lesson.n} · ${r.lesson.title_hi}`), "lesson"],
                [L("Date", "तारीख"), new Date(r.issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }), "date"]].map(([k, v, id]) => (
                <div key={id} className={id === "lesson" ? "col-span-2" : ""}><dt className="text-[11px] uppercase tracking-widest text-slate-500">{k}</dt><dd className="font-bold" data-testid={`verify-star-${id}`}>{v}</dd></div>
              ))}
            </dl>
            <Link to="/virtual-classroom" className="mt-6 block text-center text-sm font-semibold text-[#007A8A] hover:text-[#FF2E63]" data-testid="verify-star-classroom-link">{L("Try the free RoboSpark classes →", "मुफ़्त RoboSpark क्लास देखें →")}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
